<template>
  <div class="data-table" :style="{ 'height': dynamicHeight }"> 
    <div class="table-container" :style="{ 'max-height': maxBodyHeight }">
      <table class="table" aria-label="IPOs Table">
        <thead>
          <tr class="header-row">
            <th 
              v-for="(item, index) in table_header" 
              :key="index" 
              scope="col" 
              class="header-cell"
              :class="getHeaderCellClass(index)"
            >
              {{ item }}
            </th>
          </tr>
        </thead>
        <tbody class="table-content">
          <tr v-for="(row, rowIndex) in table_body" :key="rowIndex" class="table-row">
            <td 
              v-for="(cell, cellIndex) in row" 
              :key="cellIndex" 
              class="table-cell"
              :class="getBodyCellClass(cellIndex)"
              :data-label="table_header[cellIndex]"
            >
              <span v-html="cell"></span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TableNewsIpos',
  props: {
    table_header: {
      type: Array,
      required: true
    },
    table_body: {
      type: Array,
      required: true
    },
    maxBodyHeight: {
        type: String,
        default: '60vh'
    },
    dynamicHeight: {
        type: String,
        default: 'auto' // Changed default to auto, can be set to 60vh or other from parent for fixed height card
    }
  },
  methods: {
    // Helper methods to assign specific classes for alignment based on column index
    // Assuming: 0 = Ticker (center), 1 = Empresa (center), 2 = Fecha (center/right)
    getHeaderCellClass(index) {
      if (index === 0 || index === 1) return 'header-cell--center';
      if (index === 2) return 'header-cell--center'; // Or 'header-cell--right'
      return 'header-cell--center'; // Default
    },
    getBodyCellClass(index) {
      if (index === 0 || index === 1) return 'table-cell--center';
      if (index === 2) return 'table-cell--center'; // Or 'table-cell--right'
      return 'table-cell--center'; // Default
    }
  }
}
</script>

<style lang="scss" scoped>
// Base styles from TableIndex.vue and TableSector.vue, adapted for NewsIPOs
.data-table {
  width: 100%;
  border-radius: 8px;
  background-color: var(--color-surface);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  margin-bottom: 32px;
  overflow: hidden; // Important for child border-radius to be effective if table-container has no border
  border: 1px solid var(--color-outline);
  transition: all 0.2s ease;
}

.table-container {
  width: 100%;
  overflow-x: auto; // Horizontal scroll for table content
  overflow-y: auto; // Vertical scroll for table content if maxBodyHeight is less than content
  scrollbar-width: thin;
  scrollbar-color: var(--color-muted) var(--color-surface);
  
  &::-webkit-scrollbar {
    height: 6px;
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: var(--color-surface);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background-color: var(--color-muted);
    border-radius: 3px;
    border: 1px solid var(--color-surface);
  }
}

.table {
  width: 100%;
  border-collapse: separate; // Use separate for border-spacing to work, or collapse if preferred
  border-spacing: 0;
  white-space: nowrap;
  font-size: 13px; // Match TableIndex
  // table-layout: fixed; // Enable if specific column widths are set and desired
}

.header-row {
  height: 44px;
  background-color: var(--color-background); // Match TableIndex
  border-bottom: 1px solid var(--color-outline); // Match TableIndex
  position: -webkit-sticky; // For Safari
  position: sticky;
  top: 0;
  z-index: 1;
}

.header-cell {
  color: var(--color-text-secondary);
  font-weight: 600;
  text-transform: uppercase;
  padding: 0 12px;
  height: 44px;
  letter-spacing: 0.3px;
  font-size: 11px; // Match TableIndex
  vertical-align: middle;
  // Default alignment, can be overridden by specific classes
  text-align: left; 

  // Specific widths if needed, similar to TableIndex.vue
  // For NewsIPOs: Ticker, Empresa, Fecha Publicación
  &:nth-child(1) { /* Ticker */
    width: 20%; 
    text-align: center; 
  }
  &:nth-child(2) { /* Empresa */
    width: 50%; 
    white-space: normal; // Allow company names to wrap
    word-break: break-word;
    text-align: center; 
  }
  &:nth-child(3) { /* Fecha Publicación */
    width: 30%;
    text-align: center; // Center date by default
  }

  &--left { text-align: left; }
  &--center { text-align: center; }
  &--right { text-align: right; }
}

.table-content { /* Corresponds to tbody */
  // tbody specific styles if any
}

.table-row {
  height: 40px;
  border-bottom: 1px solid var(--color-outline);
  transition: background-color 0.2s ease;
  
  &:nth-child(even) {
    background-color: var(--color-background-muted, rgba(0,0,0,0.02)); // Subtle zebra striping
  }
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background-color: var(--color-active-surface);
  }
}

.table-cell {
  padding: 6px 12px;
  height: 40px;
  color: var(--color-text-primary);
  font-size: 13px; // Match TableIndex
  vertical-align: middle;
  // Default alignment, can be overridden by specific classes
  text-align: left;

  span {
    word-break: break-word;
  }
  
  &:nth-child(1) { /* Ticker */
    text-align: center;
  }
  &:nth-child(2) { /* Empresa */
    white-space: normal; 
    text-align: center;
  }
  &:nth-child(3) { /* Fecha Publicación */
    text-align: center; // Center date by default
  }

  &--left { text-align: left; }
  &--center { text-align: center; }
  &--right { text-align: right; }
}

// Responsive (stacked table) styles from previous refactor, adapted
@media screen and (max-width: 700px) { // Adjusted breakpoint for better stacking experience
  .header-row { // Hide traditional header
    display: none;
  }

  .table, .table-content, .table-row, .table-cell {
    display: block;
    width: 100%;
  }

  .table-row {
    margin-bottom: 10px;
    border: 1px solid var(--color-outline);
    border-radius: 6px;
    padding: 10px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.05);
    height: auto; // Adjust height for stacked content
    &:nth-child(even) {
      background-color: var(--color-surface); // No zebra when stacked and card-like
    }
  }

  .table-cell {
    padding: 8px 0; 
    border-bottom: none; 
    text-align: right; 
    position: relative;
    padding-left: 45%; // Space for the label
    height: auto; // Adjust height
    white-space: normal; // Allow content to wrap
    word-break: break-word;

    &::before {
      content: attr(data-label); 
      position: absolute;
      left: 10px;
      width: calc(45% - 20px); 
      padding-right: 10px;
      font-weight: 600;
      text-align: left;
      color: var(--color-text-primary);
      text-transform: uppercase;
      font-size: 0.75rem;
      white-space: nowrap; // Prevent label from wrapping
    }
    // Ensure specific cell alignments are reset or adjusted for stacked view if needed
    &--left, &--center, &--right {
        text-align: right; // Force right align for value part in stacked view
    }
  }
}

</style>
